import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  GtmEventUser,
  ITicketProductWithBundleType,
  LoadingDetails,
} from '@nx-ng/types';
import { combineLatest, Observable, of } from 'rxjs';
import { switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { TicketsAndFaresStoreFacade } from '@modules/ticket-fares-store';
import { CheckoutActions } from '../../actions';
import { Basket } from '../../client/src';
import { GtmEventService } from '@nx-ng/services/gtm-event.service';
import { Event, NavigationEnd, Router } from '@angular/router';
import { B2CEventsService } from '@nx-ng/b2c/b2c-events.service';
import { CustomerStoreFacade } from '@modules/customer-store';
import { CreateBasketItem } from '@modules/checkout-store/types';
import { debounce } from 'lodash-es';

const DEBOUNCE_GTM_PAGE_CHANGE_EVENT_MS = 1000;

@Injectable()
export class GtmEffects {
  private prevLoggedInUser: GtmEventUser | undefined = undefined;

  private recordPageEvents = false;

  enableRecordOfPageEvents$ = createEffect(
    () =>
      this.b2cService.isLoggedIn$.pipe(
        tap((isLoggedIn: boolean | undefined) => {
          if (isLoggedIn !== undefined) {
            this.recordPageEvents = true;
          }
        })
      ),
    { dispatch: false }
  );

  private sendPageChangeEvent: (userDetails: GtmEventUser) => void = () => {};

  private userDetails$: Observable<GtmEventUser> = combineLatest([
    this.b2cService.isLoggedIn$,
    this.customerStoreFacade.customerId$,
  ]).pipe(
    switchMap(
      ([isLoggedIn, customerId]: [boolean | undefined, string | undefined]) => {
        const gtmUser: GtmEventUser = {
          loginState: isLoggedIn,
          id: customerId,
          loginMethod: 'email',
        };

        return of(gtmUser);
      }
    )
  );

  pageChange$ = createEffect(
    () =>
      combineLatest([this.router.events, this.userDetails$]).pipe(
        tap(([event, userDetails]: [Event, GtmEventUser]) => {
          if (event instanceof NavigationEnd && this.recordPageEvents) {
            this.sendPageChangeEvent(userDetails);
          }
        })
      ),
    {
      dispatch: false,
    }
  );

  loginChanged$ = createEffect(
    () =>
      this.userDetails$.pipe(
        tap((userDetails: GtmEventUser) => {
          if (
            userDetails.loginState === undefined ||
            this.prevLoggedInUser === userDetails
          ) {
            return;
          }

          if (userDetails.loginState && userDetails.id) {
            this.sendPageChangeEvent(userDetails);
            this.prevLoggedInUser = userDetails;
          } else if (!userDetails.loginState) {
            this.sendPageChangeEvent(userDetails);
            this.prevLoggedInUser = userDetails;
          }
        })
      ),
    {
      dispatch: false,
    }
  );

  basketUpdateEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.sendBasketEventToGtm),

        switchMap((action) => {
          return combineLatest([
            of(action.basketPriorToEvent),
            of(action.basketItem),
            this.userDetails$,
            this.ticketAndFaresFacade.allFares$,
          ]).pipe(take(1));
        }),
        tap(
          ([basketPriorToEvent, basketItem, userDetails, allTickets]: [
            Basket | undefined,
            CreateBasketItem,
            GtmEventUser,
            ITicketProductWithBundleType[]
          ]) => {
            this.gtmEventService.sendBasketEvent({
              basketItem,
              allTickets,
              userDetails,
              basket: basketPriorToEvent,
            });
          }
        )
      ),
    {
      dispatch: false,
    }
  );

  constructor(
    private readonly actions$: Actions,
    private ticketAndFaresFacade: TicketsAndFaresStoreFacade,
    private gtmEventService: GtmEventService,
    private router: Router,
    private b2cService: B2CEventsService,
    private customerStoreFacade: CustomerStoreFacade
  ) {
    this.sendPageChangeEvent = debounce(
      this.gtmEventService.sendPageChangeEvent.bind(this.gtmEventService),
      DEBOUNCE_GTM_PAGE_CHANGE_EVENT_MS
    );
  }
}
